import { useId } from "react";
import { defineStrings, useStrings } from "../i18n";
import { Icon } from "./Icon";

const strings = defineStrings(
  { right: "right", notYet: "not yet" },
  { right: "juste", notYet: "pas encore" },
);

export interface Choice {
  readonly value: string;
  readonly label: string;
}

/**
 * Engine verdict for a choice, shown only after the actor (or the local salted check)
 * has graded it. The component never decides it.
 */
export type ChoiceVerdict = "right" | "not-yet";

export interface ChoiceGroupProps {
  readonly legend: string;
  readonly choices: readonly Choice[];
  readonly value: string | null;
  readonly onChange: (value: string) => void;
  readonly verdict?: { readonly value: string; readonly verdict: ChoiceVerdict };
  readonly disabled?: boolean;
  /** Two columns from 600px wide when every label is short. */
  readonly columns?: 1 | 2;
}

/**
 * Big, tappable answer cards built on native radio buttons, so arrow keys, screen
 * readers and form semantics work without extra code. Verdicts show an icon and a word,
 * never colour alone.
 */
export function ChoiceGroup(props: ChoiceGroupProps) {
  const name = useId();
  const t = useStrings(strings);
  return (
    <fieldset className="ui-choices" data-columns={props.columns ?? 1} disabled={props.disabled}>
      <legend className="ui-choices__legend">{props.legend}</legend>
      <div className="ui-choices__grid">
        {props.choices.map((choice) => {
          const verdict = props.verdict?.value === choice.value ? props.verdict.verdict : undefined;
          return (
            <label key={choice.value} className="ui-choice" data-verdict={verdict}>
              <input
                type="radio"
                name={name}
                value={choice.value}
                checked={props.value === choice.value}
                onChange={() => {
                  props.onChange(choice.value);
                }}
              />
              <span className="ui-choice__label">{choice.label}</span>
              {verdict !== undefined && (
                <span className="ui-choice__verdict">
                  <Icon name={verdict === "right" ? "check" : "heart"} size={20} />
                  {t(verdict === "right" ? "right" : "notYet")}
                </span>
              )}
            </label>
          );
        })}
      </div>
    </fieldset>
  );
}
